import { useEffect, useState } from 'react';
import { Modal, Form, Input, Tabs, Card, Space, Typography, App, Divider, Alert, Select } from 'antd';
import { NotificationType, NotificationChannel, NotificationTemplate } from '@mygate/common';
import { notificationsService, UpdateNotificationTypeRequest } from '@/services/notifications';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface Props {
  visible: boolean;
  notificationType: NotificationType | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface TemplateFormValues {
  title: string;
  body: string;
  deepLink?: string;
  whatsappTemplateId?: string;
}

interface FormValues {
  name: string;
  description: string;
  primaryChannel: NotificationChannel;
  fallbackChain: NotificationChannel[];
  templateDE: TemplateFormValues;
  templateEN: TemplateFormValues;
}

export function NotificationTypeEditorModal({ visible, notificationType, onClose, onSuccess }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();

  // Reset form when modal opens with new data
  useEffect(() => {
    if (visible && notificationType) {
      const templates = notificationType.templates as Record<string, NotificationTemplate>;
      const deTemplate = templates?.de || { title: '', body: '' };
      const enTemplate = templates?.en || { title: '', body: '' };

      form.setFieldsValue({
        name: notificationType.name,
        description: notificationType.description || '',
        primaryChannel: notificationType.channels.primary,
        fallbackChain: notificationType.channels.fallbackChain || [],
        templateDE: {
          title: deTemplate.title || '',
          body: deTemplate.body || '',
          deepLink: deTemplate.deepLink || '',
          whatsappTemplateId: deTemplate.whatsappTemplateId || '',
        },
        templateEN: {
          title: enTemplate.title || '',
          body: enTemplate.body || '',
          deepLink: enTemplate.deepLink || '',
          whatsappTemplateId: enTemplate.whatsappTemplateId || '',
        },
      });
    }
  }, [visible, notificationType, form]);

  const handleSubmit = async () => {
    if (!notificationType) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      const updateData: UpdateNotificationTypeRequest = {
        channels: {
          primary: values.primaryChannel,
          fallbackChain: values.fallbackChain || [],
        },
        templates: {
          de: {
            title: values.templateDE.title,
            body: values.templateDE.body,
            deepLink: values.templateDE.deepLink,
            whatsappTemplateId: values.templateDE.whatsappTemplateId,
          },
          en: {
            title: values.templateEN.title,
            body: values.templateEN.body,
            deepLink: values.templateEN.deepLink,
            whatsappTemplateId: values.templateEN.whatsappTemplateId,
          },
        },
      };

      const response = await notificationsService.updateNotificationType(notificationType.code, updateData);

      if (response.success) {
        message.success('Notification type updated successfully');
        onSuccess();
      }
    } catch (error) {
      message.error('Failed to update notification type');
      console.error('Error updating notification type:', error);
    } finally {
      setLoading(false);
    }
  };

  const channelOptions = Object.values(NotificationChannel).map(ch => ({
    label: ch,
    value: ch,
  }));

  // General tab content
  const GeneralTab = (
    <div>
      <Form.Item name="name" label="Name">
        <Input disabled />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <TextArea rows={2} disabled />
      </Form.Item>

      <Divider>Channel Configuration</Divider>

      <Form.Item
        name="primaryChannel"
        label="Primary Channel"
        rules={[{ required: true, message: 'Primary channel is required' }]}
      >
        <Select options={channelOptions} />
      </Form.Item>

      <Form.Item name="fallbackChain" label="Fallback Chain">
        <Select mode="multiple" options={channelOptions} placeholder="Select fallback channels in order" />
      </Form.Item>

      {notificationType && (
        <Alert
          message="Notification Type Info"
          description={
            <div>
              <div>
                <Text strong>Code:</Text> <Text code>{notificationType.code}</Text>
              </div>
              <div>
                <Text strong>Category:</Text> {notificationType.category}
              </div>
              <div>
                <Text strong>Enabled:</Text> {notificationType.isEnabled ? 'Yes' : 'No'}
              </div>
            </div>
          }
          type="info"
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );

  // Template editor component
  const TemplateEditor = ({ language, prefix }: { language: string; prefix: string }) => (
    <Card title={`${language} Template`} size="small" style={{ marginBottom: 16 }}>
      <Form.Item name={[prefix, 'title']} label="Title" rules={[{ required: true, message: 'Title is required' }]}>
        <Input placeholder="Notification title" />
      </Form.Item>
      <Form.Item
        name={[prefix, 'body']}
        label="Body"
        rules={[{ required: true, message: 'Body is required' }]}
        extra="Use {{placeholder}} for dynamic content"
      >
        <TextArea rows={4} placeholder="Notification body..." />
      </Form.Item>
      <Form.Item name={[prefix, 'deepLink']} label="Deep Link" extra="Optional: app deep link URL">
        <Input placeholder="mygate://screen/path" />
      </Form.Item>
      <Form.Item
        name={[prefix, 'whatsappTemplateId']}
        label="WhatsApp Template ID"
        extra="Vonage template ID for WhatsApp channel"
      >
        <Input placeholder="template_id" />
      </Form.Item>
    </Card>
  );

  // Templates tab content
  const TemplatesTab = (
    <div>
      <TemplateEditor language="German (DE)" prefix="templateDE" />
      <TemplateEditor language="English (EN)" prefix="templateEN" />
    </div>
  );

  // Placeholders tab content
  const PlaceholdersTab = (
    <div>
      <Alert
        message="Available Placeholders"
        description={
          <div>
            <p>These placeholders are available in templates and will be replaced with actual values:</p>
            <Divider />
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text code>{'{{firstName}}'}</Text> - User's first name
              </div>
              <div>
                <Text code>{'{{lastName}}'}</Text> - User's last name
              </div>
              <div>
                <Text code>{'{{amount}}'}</Text> - Transaction amount
              </div>
              <div>
                <Text code>{'{{currency}}'}</Text> - Currency code
              </div>
              <div>
                <Text code>{'{{date}}'}</Text> - Date of event
              </div>
              <div>
                <Text code>{'{{balance}}'}</Text> - Account balance
              </div>
            </Space>
            <Divider />
            {notificationType?.placeholders && notificationType.placeholders.length > 0 && (
              <>
                <Title level={5}>Type-Specific Placeholders</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {notificationType.placeholders.map(p => (
                    <div key={p.key}>
                      <Text code>{`{{${p.key}}}`}</Text> - {p.description}
                      {p.required && <Text type="danger"> (required)</Text>}
                    </div>
                  ))}
                </Space>
              </>
            )}
            <Text type="secondary">
              Note: Available placeholders depend on the notification type. Check the specification for details.
            </Text>
          </div>
        }
        type="info"
      />
    </div>
  );

  const tabItems = [
    { key: 'general', label: 'General', children: GeneralTab },
    { key: 'templates', label: 'Templates', children: TemplatesTab },
    { key: 'placeholders', label: 'Placeholders', children: PlaceholdersTab },
  ];

  return (
    <Modal
      title={
        <Space wrap>
          <span>Edit Notification Type</span>
          {notificationType && <Text code>{notificationType.code}</Text>}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Tabs items={tabItems} />
      </Form>
    </Modal>
  );
}
